// Client health watchdog (owner 2026-10-30: a Safari tab grew slower and slower until a
// restart; keep an eye on whether the web client degrades over time). A one-second tick
// measures how late it fires: a late tick means the main thread was blocked. Stalls over the
// threshold are kept in a short ring with the screen they happened on, shown under
// Inställningar → Diagnostik and written to the console, so the next slow tab can be read
// before it is restarted. Safari has no long-task observer, so the tick is the portable way.

export interface Stall {
  at: string;
  /** Seconds the tick arrived late. */
  seconds: number;
  screen: string;
}

const STALL_SECONDS = 2;
const KEEP = 50;
const stalls: Stall[] = [];
let ticks = 0;
let worst = 0;
let startedAt = 0;
let screen = "start";
let handle: number | null = null;

/** The current screen, for the record (set by the app when it changes). */
export function setHealthScreen(name: string): void {
  screen = name;
}

export function startHealthWatchdog(): void {
  if (handle !== null) return;
  startedAt = Date.now();
  let expected = Date.now() + 1000;
  handle = window.setInterval(() => {
    const now = Date.now();
    const late = (now - expected) / 1000;
    expected = now + 1000;
    ticks++;
    if (late > worst) worst = late;
    if (late >= STALL_SECONDS) {
      stalls.push({ at: new Date(now).toISOString(), seconds: Math.round(late * 10) / 10, screen });
      if (stalls.length > KEEP) stalls.shift();
      console.warn(`[trail health] main thread stalled ${late.toFixed(1)} s on ${screen}`);
    }
  }, 1000);
}

export interface HealthSnapshot {
  uptimeMinutes: number;
  stallsLastHour: number;
  worstSeconds: number;
  memoryMb: number | null;
  recent: Stall[];
}

export function healthSnapshot(): HealthSnapshot {
  const hourAgo = Date.now() - 3600_000;
  const perf = performance as Performance & { memory?: { usedJSHeapSize: number } };
  return {
    uptimeMinutes: startedAt ? Math.round((Date.now() - startedAt) / 60_000) : 0,
    stallsLastHour: stalls.filter((s) => Date.parse(s.at) >= hourAgo).length,
    worstSeconds: Math.round(worst * 10) / 10,
    memoryMb: perf.memory ? Math.round(perf.memory.usedJSHeapSize / 1_048_576) : null,
    recent: stalls.slice(-5),
  };
}

/** For tests: forget everything. */
export function resetHealth(): void {
  stalls.length = 0;
  ticks = 0;
  worst = 0;
}
